import type { ServiceEntry } from './types';

/**
 * Single subsystem entry — shape used in VITE_SERVICES_CONFIG (JSON array).
 *
 * Example env value:
 *   VITE_SERVICES_CONFIG='[
 *     {"id":"monitoring","url":"/monitoring/","name":"资产监控","icon":"Monitor","order":10,"desc":"跨链桥资产、TVL、告警"},
 *     {"id":"admin","url":"/admin/","name":"成员管理","icon":"Users","order":60,"comingSoon":true}
 *   ]'
 */
export interface ServiceConfigEntry {
  id: string;
  url: string;
  name: string;
  icon?: string;
  desc?: string;
  order?: number;
  comingSoon?: boolean;
}

/**
 * Parse VITE_SERVICES_CONFIG (JSON array) into ServiceEntry[].
 *
 * Single source of truth — same env value drives shell Portal cards,
 * subsystem ServiceSwitcher dropdown, and (in the future) any other place
 * that needs to enumerate subsystems. No code-side catalog required.
 *
 * Returns [] on missing/invalid input (with console.warn).
 */
export function parseServicesConfig(
  raw: string | undefined | null,
): ServiceEntry[] {
  if (!raw || raw.trim() === '') return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    console.warn('[services] VITE_SERVICES_CONFIG is not valid JSON:', e);
    return [];
  }

  if (!Array.isArray(parsed)) {
    console.warn('[services] VITE_SERVICES_CONFIG must be a JSON array');
    return [];
  }

  const seen = new Map<string, ServiceEntry>();
  for (const item of parsed) {
    if (!item || typeof item !== 'object') continue;
    const raw = item as Partial<ServiceConfigEntry>;
    const id = typeof raw.id === 'string' ? raw.id.trim() : '';
    const url = typeof raw.url === 'string' ? raw.url.trim() : '';
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    if (!id || !url || !name) {
      console.warn('[services] dropping entry — missing id/url/name', raw);
      continue;
    }
    seen.set(id, {
      id,
      url,
      name,
      iconName: typeof raw.icon === 'string' && raw.icon ? raw.icon : undefined,
      desc: typeof raw.desc === 'string' ? raw.desc : undefined,
      order: typeof raw.order === 'number' ? raw.order : 1000,
      comingSoon: Boolean(raw.comingSoon),
    });
  }

  return Array.from(seen.values()).sort(
    (a, b) => (a.order ?? 1000) - (b.order ?? 1000),
  );
}
